import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { policies } from '../services/api.js';
import { CommissionManagement } from './CommissionManagement.jsx';
import { ComplianceFiling } from './ComplianceFiling.jsx';
import { ReinsuranceTreaty } from './ReinsuranceTreaty.jsx';
import './PolicyManagement.css';

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStatusBadgeClass(status) {
  switch (status?.toLowerCase()) {
    case 'active': return 'pom__badge pom__badge--active';
    case 'issued': return 'pom__badge pom__badge--issued';
    case 'expired': return 'pom__badge pom__badge--expired';
    case 'cancelled': return 'pom__badge pom__badge--cancelled';
    default: return 'pom__badge';
  }
}

export function PolicyManagement() {
  const [hubTab, setHubTab] = useState('policies');
  const [policyList, setPolicyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const result = await policies.list();
        const data = result.data || result;
        setPolicyList(Array.isArray(data) ? data : data.items || []);
      } catch (err) {
        console.error('Failed to load policies:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredPolicies = useMemo(() => {
    return policyList.filter((p) => {
      const matchesSearch =
        !search.trim() ||
        (p.policyNumber || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.insuredName || p.InsuredName || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.productId || p.ProductID || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (p.status || p.Status || '').toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [policyList, search, statusFilter]);

  const stats = useMemo(() => {
    const total = policyList.length;
    const active = policyList.filter((p) => (p.status || p.Status) === 'active').length;
    const writtenPremium = policyList.reduce((sum, p) => sum + (p.premium || p.Premium || 0), 0);
    const avgPremium = total > 0 ? Math.round(writtenPremium / total) : 0;
    return { total, active, writtenPremium, avgPremium };
  }, [policyList]);

  if (loading) {
    return (
      <div className="pom">
        <h1 className="pom__title">Policy Management</h1>
        <p style={{ textAlign: 'center', padding: '2rem' }}>Loading policies...</p>
      </div>
    );
  }

  return (
    <div className="pom">
      {/* Hub Tab Bar */}
      <div className="hub-tabs">
        <button className={`hub-tab ${hubTab === 'policies' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('policies')}>Policies</button>
        <button className={`hub-tab ${hubTab === 'commissions' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('commissions')}>Commissions</button>
        <button className={`hub-tab ${hubTab === 'compliance' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('compliance')}>Compliance</button>
        <button className={`hub-tab ${hubTab === 'treaties' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('treaties')}>Treaties</button>
      </div>

      {hubTab === 'commissions' && <CommissionManagement />}
      {hubTab === 'compliance' && <ComplianceFiling />}
      {hubTab === 'treaties' && <ReinsuranceTreaty />}
      {hubTab === 'policies' && (
      <>
      <h1 className="pom__title">Policy Management</h1>

      {/* Stat cards */}
      <div className="pom__stats">
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Total Policies</span>
          <span className="pom__stat-value">{stats.total}</span>
        </div>
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Active</span>
          <span className="pom__stat-value pom__stat-value--green">{stats.active}</span>
        </div>
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Written Premium</span>
          <span className="pom__stat-value">{formatCurrency(stats.writtenPremium)}</span>
        </div>
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Avg Premium</span>
          <span className="pom__stat-value">{formatCurrency(stats.avgPremium)}</span>
        </div>
      </div>

      {/* Search and filter */}
      <div className="pom__filters">
        <div className="pom__search-wrap">
          <input
            className="pom__search"
            type="text"
            placeholder="Search by policy #, insured, or product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="pom__search-clear" onClick={() => setSearch('')}>
              &times;
            </button>
          )}
        </div>
        <select
          className="pom__filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <div className="pom__section">
        <h2 className="pom__section-title">Policies</h2>
        <div className="pom__table-wrap card">
          <table className="pom__table">
            <thead>
              <tr>
                <th>Policy #</th>
                <th>Insured Name</th>
                <th>Product</th>
                <th>Premium</th>
                <th>Limit</th>
                <th>Effective Date</th>
                <th>Expiration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPolicies.length > 0 ? (
                filteredPolicies.map((p) => (
                  <tr key={p.policyId || p.PolicyID} className="pom__table-row">
                    <td className="pom__table-policy">
                      <Link to={`/accounts/${p.accountId || p.AccountID}`}>
                        {p.policyNumber || p.PolicyNumber}
                      </Link>
                    </td>
                    <td className="pom__table-insured">{p.insuredName || p.InsuredName || '—'}</td>
                    <td>{p.productId || p.ProductID}</td>
                    <td className="pom__table-currency">{formatCurrency(p.premium || p.Premium)}</td>
                    <td className="pom__table-currency">{formatCurrency(p.limit || p.Limit)}</td>
                    <td className="pom__table-date">{formatDate(p.effectiveDate || p.EffectiveDate)}</td>
                    <td className="pom__table-date">{formatDate(p.expirationDate || p.ExpirationDate)}</td>
                    <td>
                      <span className={getStatusBadgeClass(p.status || p.Status)}>
                        {p.status || p.Status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="pom__table-empty">
                    {policyList.length === 0
                      ? 'No policies issued yet. Policies are created when a quote is accepted.'
                      : 'No policies match your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
